<!--
  Anfragen: Liste, Einzelansicht und das Formular für eine neue.

  Die Felder heißen wie im Gespräch: „Worum geht es?" statt „Titel",
  „Beschreiben Sie es ruhig in eigenen Worten" statt „Anliegen".
-->
<script lang="ts">
  import { api } from '../lib/api.ts';
  import HilfeLink from '../lib/HilfeLink.svelte';
  import { datum, zeitpunkt } from '../lib/sitzung.svelte.ts';

  let { id }: { id?: string } = $props();

  interface Nachricht {
    id: string;
    richtung: 'kunde' | 'intern';
    text: string;
    gesendet_am: string;
  }

  interface Ticket {
    id: string;
    nummer: string;
    titel: string;
    status: string;
    anliegen: string | null;
    arbeiten: string | null;
    ergebnis: string | null;
    /** Verlauf (Migration 039) — nur kundensichtbare Einträge, chronologisch. */
    eintraege?: { id: string; kategorie: string; titel: string; text: string; erstellt_am: string }[];
    eroeffnet_am: string;
    geschlossen_am: string | null;
    verbrauchte_einheiten: number | null;
    termine?: { id: string; beginn: string; art: string; status: string }[];
    nachrichten?: Nachricht[];
  }

  const STATUSTEXT: Record<string, string> = {
    neu: 'aufgenommen',
    in_bearbeitung: 'wird bearbeitet',
    wartet: 'wartet auf etwas',
    geschlossen: 'erledigt',
    abgebrochen: 'abgebrochen',
  };

  let tickets = $state<Ticket[]>([]);
  let einzeln = $state<Ticket | null>(null);
  let fehler = $state<string | null>(null);
  let hinweis = $state<string | null>(null);
  let zeigeFormular = $state(false);
  let sendet = $state(false);
  let neu = $state({ titel: '', anliegen: '' });
  let neueNachricht = $state('');
  let sendetNachricht = $state(false);
  /** Kennung der gerade angelegten Anfrage — für „gleich einen Termin dazu". */
  let geradeAngelegt = $state<{ id: string; nummer: string } | null>(null);

  /** Anfragen, die noch laufen, können einen Termin bekommen. */
  function nochOffen(t: Ticket): boolean {
    return t.status !== 'erledigt' && t.status !== 'archiviert' && t.status !== 'geschlossen' && !t.geschlossen_am;
  }

  /**
   * Zur Terminwahl mit vorbelegter Anfrage — der Weg, den ein Kunde braucht,
   * der erst schreibt und dann einen Termin will. Vorher entstand daraus ein
   * zweites Ticket (T-1041/T-1042, 04.09.2026).
   */
  function terminDazu(ticketId: string): void {
    location.hash = `/termine/anfrage/${ticketId}`;
  }

  async function laden(): Promise<void> {
    try {
      if (id) {
        einzeln = await api.get<Ticket>(`/api/portal/tickets/${id}`);
      } else {
        tickets = (await api.get<{ tickets: Ticket[] }>('/api/portal/tickets')).tickets;
      }
      fehler = null;
    } catch (f) {
      fehler = f instanceof Error ? f.message : 'Das konnten wir gerade nicht laden';
    }
  }

  $effect(() => {
    void id;
    void laden();
  });

  async function absenden(ereignis: SubmitEvent): Promise<void> {
    ereignis.preventDefault();
    sendet = true;
    fehler = null;
    try {
      const angelegt = await api.post<{ id: string; nummer: string }>('/api/portal/anfragen', {
        titel: neu.titel.trim(),
        anliegen: neu.anliegen.trim(),
      });
      hinweis = `Ihre Anfrage ist bei uns angekommen (${angelegt.nummer}). Wir melden uns per E-Mail und hier im Kundenbereich — schauen Sie bitte auch in den Spam-Ordner.`;
      geradeAngelegt = angelegt;
      neu = { titel: '', anliegen: '' };
      zeigeFormular = false;
      await laden();
    } catch (f) {
      fehler = f instanceof Error ? f.message : 'Das hat gerade nicht geklappt';
    } finally {
      sendet = false;
    }
  }

  async function nachrichtSenden(ereignis: SubmitEvent): Promise<void> {
    ereignis.preventDefault();
    if (!neueNachricht.trim() || !id) return;
    sendetNachricht = true;
    fehler = null;
    try {
      await api.post(`/api/portal/tickets/${id}/nachrichten`, { text: neueNachricht.trim() });
      neueNachricht = '';
      await laden();
    } catch (f) {
      fehler = f instanceof Error ? f.message : 'Nachricht senden fehlgeschlagen';
    } finally {
      sendetNachricht = false;
    }
  }
</script>

{#if id}
  <p><a href="#/anfragen">← Alle Anfragen</a></p>
  {#if fehler}<p class="fehler">{fehler}</p>{/if}
  {#if einzeln}
    <h1>{einzeln.titel}</h1>
    <p class="leise">{einzeln.nummer} · aufgenommen am {datum(einzeln.eroeffnet_am)}</p>

    <div class="karte">
      <p><span class="marke">{STATUSTEXT[einzeln.status] ?? einzeln.status}</span></p>

      {#if einzeln.eintraege && einzeln.eintraege.length > 0}
        {#each einzeln.eintraege as eintrag (eintrag.id)}
          <h3>
            {eintrag.kategorie === 'arbeiten' && eintrag.titel === 'Durchgeführte Arbeiten'
              ? 'Was wir gemacht haben'
              : eintrag.titel === 'Anliegen'
                ? 'Ihr Anliegen'
                : eintrag.titel}
            <span class="leise klein">· {datum(eintrag.erstellt_am)}</span>
          </h3>
          <p style="white-space: pre-wrap">{eintrag.text}</p>
        {/each}
      {:else}
        {#if einzeln.anliegen}
          <h3>Ihr Anliegen</h3>
          <p>{einzeln.anliegen}</p>
        {/if}
        {#if einzeln.arbeiten}
          <h3>Was wir gemacht haben</h3>
          <p>{einzeln.arbeiten}</p>
        {/if}
        {#if einzeln.ergebnis}
          <h3>Ergebnis</h3>
          <p>{einzeln.ergebnis}</p>
        {/if}
      {/if}

      {#if Number(einzeln.verbrauchte_einheiten ?? 0) > 0}
        <p class="leise klein">
          Dafür wurden {Number(einzeln.verbrauchte_einheiten).toLocaleString('de-DE')} Einheiten
          verwendet.
        </p>
      {/if}

      {#if einzeln.termine && einzeln.termine.length > 0}
        <h3>Termine dazu</h3>
        <ul class="eintraege">
          {#each einzeln.termine as termin (termin.id)}
            <li>{zeitpunkt(termin.beginn)}</li>
          {/each}
        </ul>
      {/if}

      {#if nochOffen(einzeln)}
        <div class="knopfreihe">
          <button type="button" class="sekundaer" onclick={() => terminDazu(einzeln!.id)}>
            Termin dazu vorschlagen
          </button>
        </div>
      {/if}
    </div>

    <div class="karte">
      <h2>Nachrichten</h2>
      {#if !einzeln.nachrichten || einzeln.nachrichten.length === 0}
        <p class="leise">Hier erscheint der Nachrichtenverlauf zu dieser Anfrage.</p>
      {:else}
        <ul class="nachrichtenverlauf">
          {#each einzeln.nachrichten as n (n.id)}
            <li class="nachricht" class:von-uns={n.richtung === 'intern'}>
              <div class="leise klein">
                {n.richtung === 'intern' ? 'IT-Service Karsten Fischer' : 'Sie'} · {zeitpunkt(n.gesendet_am)}
              </div>
              <p>{n.text}</p>
            </li>
          {/each}
        </ul>
      {/if}

      <form onsubmit={nachrichtSenden}>
        <label for="nachricht">Nachricht schreiben</label>
        <textarea id="nachricht" bind:value={neueNachricht} required></textarea>
        <div class="knopfreihe">
          <button type="submit" disabled={sendetNachricht || !neueNachricht.trim()}>
            {sendetNachricht ? 'Wird gesendet …' : 'Senden'}
          </button>
        </div>
      </form>
    </div>
  {/if}
{:else}
  <h1>Meine Anfragen</h1>
  <HilfeLink slug="kundenbereich-anfrage-termin" text="Anleitung: Anfrage stellen und Termin wünschen" />

  {#if hinweis}<p class="erfolg">{hinweis}</p>{/if}
  {#if geradeAngelegt}
    <!-- Der häufigste nächste Wunsch nach einer Anfrage ist ein Termin. Der
         Knopf führt zur Terminwahl mit dieser Anfrage vorbelegt, damit daraus
         kein zweites Ticket wird. -->
    <div class="knopfreihe">
      <button type="button" class="sekundaer" onclick={() => terminDazu(geradeAngelegt!.id)}>
        Gleich einen Termin dazu vorschlagen
      </button>
    </div>
  {/if}
  {#if fehler}<p class="fehler">{fehler}</p>{/if}

  {#if !zeigeFormular}
    <div class="knopfreihe">
      <button type="button" onclick={() => (zeigeFormular = true)}>Neue Anfrage stellen</button>
    </div>
  {:else}
    <div class="karte">
      <h2>Was können wir für Sie tun?</h2>
      <form onsubmit={absenden}>
        <label for="titel">Worum geht es?</label>
        <input id="titel" type="text" bind:value={neu.titel} placeholder="z. B. Drucker druckt nicht" />

        <label for="anliegen">Beschreiben Sie es ruhig in eigenen Worten</label>
        <textarea
          id="anliegen"
          bind:value={neu.anliegen}
          placeholder="Seit wann? Was haben Sie schon versucht? Was steht auf dem Bildschirm?"
        ></textarea>

        <div class="knopfreihe">
          <button type="submit" disabled={sendet || !neu.titel.trim()}>
            {sendet ? 'Wird gesendet …' : 'Anfrage absenden'}
          </button>
          <button type="button" class="sekundaer" onclick={() => (zeigeFormular = false)}>
            Abbrechen
          </button>
        </div>
      </form>
    </div>
  {/if}

  <div class="karte">
    {#if tickets.length === 0}
      <p class="leise">Hier erscheinen alle Anfragen, die Sie uns gestellt haben.</p>
    {:else}
      <ul class="eintraege">
        {#each tickets as ticket (ticket.id)}
          <li>
            <div class="zeile">
              <a href="#/anfragen/{ticket.id}"><strong>{ticket.titel}</strong></a>
              <span class="marke" class:gut={ticket.status === 'geschlossen'}>
                {STATUSTEXT[ticket.status] ?? ticket.status}
              </span>
            </div>
            <div class="leise klein">
              {ticket.nummer} · {datum(ticket.eroeffnet_am)}
              {#if ticket.geschlossen_am} · erledigt am {datum(ticket.geschlossen_am)}{/if}
            </div>
          </li>
        {/each}
      </ul>
    {/if}
  </div>
{/if}
